import { Button, Card, Container, Row, Col, Form, OverlayTrigger, Tooltip, InputGroup } from 'react-bootstrap';
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import DatePicker from "react-datepicker";
import Select from 'react-select';
import axios from 'axios';
import debounce from 'lodash/debounce';
import config from '../../config';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { v4 as uuidv4 } from 'uuid';
import { alertconfirmation } from '../../alerts/Alerts';
import { carrierOptions } from '../../utils/DropDownOptions';

const HRA = forwardRef(({ caseId, renewalCase, isDisableForUsers, initialFormValues = {}, onError = () => { } }, ref) => {

    const [hras, setHras] = useState([]);
    const [generalInfo, setGeneralInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const options = [
        { value: '30', label: '30 Days' },
        { value: '60', label: '60 Days' },
        { value: '90', label: '90 Days' },
        { value: '120', label: '120 Days' },
        { value: '0', label: 'Other' },
    ];


    const [expensesArray, setExpensesArray] = useState([]);
    
      
  
      
      // hardcode today's date as 26th Sept 2025
      const today = new Date("2025-09-26T00:00:00");

  // determine createdDate from renewalCase (could be null)
  const createdDate = renewalCase?.createdAt ? new Date(renewalCase.createdAt) : null;

  // Set expensesArray when renewalCase (or createdDate) changes.
  useEffect(() => {
    // determine which expense list to use
    const newExpenses = createdDate && createdDate >= today
      ? [
          "All IRS-Eligible Dental Expenses",
          "All IRS-Eligible Vision Expenses",
          "All IRS-Eligible Medical Expenses",
          "All IRS-Eligible Over-The-Counter (OTC) Items",
          "Deductible",
          "Coinsurance",
          "Copays",
          "Prescriptions",
          "Pediatric Dental/Vision (manual submission only)",
          "In-Network Only",
          "Out-of-Network Only",
          "In- and Out-of-Network ",
        ]
      : [
          "Copayments",
          "Deductibles",
          "Coinsurance",
          "Prescription Drugs",
          "Dental",
          "Vision",
          "Pediatric Dental/Vision (manual submission only. No card option is available for this expense)",
          "All medical expenses within the IRS Code 213(d), including over-the-counter (OTC)",
          "All medical expenses within the IRS Code 213(d), except over-the-counter (OTC)",
          "In-Network Only",
          "In-Network and Out-of-Network",
          "Other",
        ];

    // only update if different (prevents unnecessary re-renders)
    setExpensesArray(prev => {
      const same = prev.length === newExpenses.length && prev.every((v,i) => v === newExpenses[i]);
      return same ? prev : newExpenses;
    });
  }, [createdDate]);
      
    

   



  useEffect(() => {
    let cancelled = false;
    const fetchGeneralInfo = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/EBGeneralInformations/ByCaseId/${caseId}`);
        if (!cancelled) setGeneralInfo(response.data || null);
      } catch (err) {
        if (!cancelled) setError('Error fetching plans.');
      }
    };

    const fetchHra = async () => {
      // don't fetch HRA until expensesArray is set
      if (!caseId || !expensesArray || expensesArray.length === 0) return;

      try {
        const response = await axios.get(`${config.API_URL}/EBHRAPlans/ByCaseId/${caseId}`);
        const hraData = (response.data || []).map(hra => {
          // transform hra.hraExpenses into array, trimming whitespace
          const right = hra.hraExpenses
            ? hra.hraExpenses.split(',').map(s => s.trim()).filter(Boolean)
            : [];

          // leftDivisions = expensesArray minus right (compare trimmed strings)
          const left = expensesArray.filter(exp => !right.includes(exp));

          return {
            ...hra,
            leftDivisions: left,
            rightDivisions: right,
            selectedLeft: [],
            selectedRight: []
          };
        });
        if (!cancelled) setHras(hraData);
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status !== 404) setError('Error fetching case files.');
          // bubble to parent handler if provided
          if (typeof onError === 'function') onError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    fetchGeneralInfo();
    fetchHra();

    return () => { cancelled = true; };
  }, [caseId, expensesArray, config.API_URL, onError]);




    const toggleSelection = (division, side, hraIndex) => {
        setHras(prevHras => {
            const updatedHras = [...prevHras];
            const selectedHra = { ...updatedHras[hraIndex] };

            if (side === 'left') {
                // Toggle selection for the left side (available expenses)
                if (selectedHra.selectedLeft.includes(division)) {
                    selectedHra.selectedLeft = selectedHra.selectedLeft.filter(item => item !== division);
                } else {
                    selectedHra.selectedLeft = [...selectedHra.selectedLeft, division];
                }
            } else if (side === 'right') {
                // Toggle selection for the right side (HRA expenses)
                if (selectedHra.selectedRight.includes(division)) {
                    selectedHra.selectedRight = selectedHra.selectedRight.filter(item => item !== division);
                } else {
                    selectedHra.selectedRight = [...selectedHra.selectedRight, division];
                }
            }

            updatedHras[hraIndex] = selectedHra;
            return updatedHras;
        });
    };





    const moveLeftToRight = (hraIndex) => {
        setHras(prevHras => {
            const updatedHras = [...prevHras];
            const selectedHra = { ...updatedHras[hraIndex] };

            // Move selected items from left to right
            const newRightDivisions = [...selectedHra.rightDivisions, ...selectedHra.selectedLeft];
            selectedHra.rightDivisions = newRightDivisions;
            selectedHra.leftDivisions = selectedHra.leftDivisions.filter(item => !selectedHra.selectedLeft.includes(item));

            // Clear selection
            selectedHra.selectedLeft = [];

            // Update the backend with new HRA expenses
            const patchDocument = [{ op: 'replace', path: '/hraExpenses', value: newRightDivisions.join(',') }];
            debouncedUpdate(patchDocument, selectedHra.planId);




            //updatedHras[hraIndex] = selectedHra;
            updatedHras[hraIndex] = { ...selectedHra, ['hraExpenses']: newRightDivisions.join(',') };
            setHras(updatedHras);
            validateField('hraExpenses', newRightDivisions, hraIndex);
            return updatedHras;
        });
    };


    const moveRightToLeft = (hraIndex) => {
        setHras(prevHras => {
            const updatedHras = [...prevHras];
            const selectedHra = { ...updatedHras[hraIndex] };

            // Move selected items from right to left
            const newRightDivisions = selectedHra.rightDivisions.filter(item => !selectedHra.selectedRight.includes(item));
            selectedHra.rightDivisions = newRightDivisions;
            selectedHra.leftDivisions = [...selectedHra.leftDivisions, ...selectedHra.selectedRight];

            // Clear selection
            selectedHra.selectedRight = [];

            // Update the backend with new HRA expenses
            const patchDocument = [{ op: 'replace', path: '/hraExpenses', value: newRightDivisions.join(',') }];
            debouncedUpdate(patchDocument, selectedHra.planId);



            //updatedHras[hraIndex] = selectedHra;
            updatedHras[hraIndex] = { ...selectedHra, ['hraExpenses']: newRightDivisions.join(',') };
            setHras(updatedHras);
            validateField('hraExpenses', newRightDivisions, hraIndex);
            return updatedHras;
        });
    };





    const debouncedUpdate = useCallback(
        debounce(async (patchDocument, planId) => {
            try {
                const url = `${config.API_URL}/EBHRAPlans/${planId}`;
                await axios.patch(url, patchDocument);
            } catch (error) {
                console.error('Error updating field:', error);
                onError(error);
            }
        }, 500),
        [onError, validationErrors]
    );

    const validateField = (name, value, index, updateState = true) => {
        let errors = { ...validationErrors };
        let fieldError = null;

        switch (name) {
            case 'planTerminationDate':
                if (hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'Plan termination date is required.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'planStartDate':
                if (!hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'Plan start date is required.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'planName':
                if (!hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'Plan name is required.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'planEndDate':
                if (!hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'Plan end date is required.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeFunding':
                if (!hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeChildFunding':
                if (!hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeSpouseFunding':
                if (!hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeFamilyFunding':
                if (!hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeUpFront':
                if (hras[index].isUpFrontRsp && !hras[index].isAutoRenew && !hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeChildUpFront':
                if (hras[index].isUpFrontRsp && !hras[index].isAutoRenew && !hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeSpouseUpFront':
                if (hras[index].isUpFrontRsp && !hras[index].isAutoRenew && !hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeFamilyUpFront':
                if (hras[index].isUpFrontRsp && !hras[index].isAutoRenew && !hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeEmbdedded':
                if (hras[index].isEmbedded && !hras[index].isAutoRenew && !hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeChildEmbdedded':
                if (hras[index].isEmbedded && !hras[index].isAutoRenew && !hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeSpouseEmbdedded':
                if (hras[index].isEmbedded && !hras[index].isAutoRenew && !hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeFamilyEmbdedded':
                if (hras[index].isEmbedded && !hras[index].isAutoRenew && !hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'maxReimbursementEmbedded':
                if (hras[index].isEmbedded && !hras[index].isAutoRenew && !hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'unusedMaxRollAmt':
                if (hras[index].unusedFundHandling === "RolledOver" && !hras[index].isAutoRenew && !hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }
                break;
            case 'unusedFundPCT':
                if (hras[index].unusedFundHandling === "RolledOver" && !hras[index].isAutoRenew && !hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (value !== undefined && (isNaN(value) || value < 0 || value > 100)) {

                        fieldError = { [name]: 'Value must be a percentage between 0 and 100.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeCostShare':
                if (hras[index].isEECostSharing && !hras[index].isAutoRenew && !hras[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (value !== undefined && (isNaN(value) || value < 0 || value > 100)) {

                        fieldError = { [name]: 'Value must be a percentage between 0 and 100.' };
                    } else {
                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'hraExpenses':
               
                if (!hras[index].isSameExpenses && !hras[index].isTerminating && !hras[index].isAutoRenew && (!value || value.length === 0)) {
                    fieldError = { [name]: 'At least one HRA expense is required.' };
                } else {

                    if (errors[index]) delete errors[index][name];
                }
                break;

            default:
                break;
        }





        if (updateState && fieldError) {
            errors[index] = {
                ...errors[index],
                ...fieldError
            };
            setValidationErrors(errors);
        }

        return fieldError;
    };

    const validateAll = () => {
        let allValid = true;
        let updatedErrors = { ...validationErrors };

        hras.forEach((hra, index) => {

            const requiredFields = [
                'planTerminationDate',
                'planStartDate',
                'planEndDate',
                'planName',
                'employeeFunding',
                'employeeChildFunding',
                'employeeSpouseFunding',
                'employeeFamilyFunding',
                'employeeUpFront',
                'employeeChildUpFront',
                'employeeSpouseUpFront',
                'employeeFamilyUpFront',
                'employeeEmbdedded',
                'employeeChildEmbdedded',
                'employeeSpouseEmbdedded',
                'employeeFamilyEmbdedded',
                'maxReimbursementEmbedded',
                'unusedMaxRollAmt',
                'unusedFundPCT',
                'employeeCostShare',
                'hraExpenses'
            ];

            requiredFields.forEach(field => {
                const fieldError = validateField(field, hra[field], index, false);
                if (fieldError) {
                    allValid = false;
                    updatedErrors = {
                        ...updatedErrors,
                        [index]: {
                            ...updatedErrors[index],
                            ...fieldError
                        }
                    };
                }
            });
        });

        setValidationErrors(updatedErrors);
        return allValid;
    };


    useImperativeHandle(ref, () => ({
        validateAll,
    }));


    const handleInputChange = (index, e) => {
        const { name, value } = e.target;
        validateField(name, value, index);

        const updatedHras = [...hras];
        updatedHras[index] = { ...updatedHras[index], [name]: value };
        setHras(updatedHras);

        let patchDocument = [{ op: 'replace', path: `/${name}`, value: value }];

        debouncedUpdate(patchDocument, updatedHras[index].planId);
    };

    const handleRadioChange = (index, fieldName, e) => {
        const { name, value } = e.target;
        validateField(fieldName, value, index);

        const updatedHras = [...hras];
        updatedHras[index] = { ...updatedHras[index], [fieldName]: value };
        setHras(updatedHras);

        let patchDocument = [{ op: 'replace', path: `/${fieldName}`, value: value }];

        debouncedUpdate(patchDocument, updatedHras[index].planId);
    };

    const handleSelectChange = (index, name, selectedOption) => {
        const value = selectedOption ? selectedOption.value : '';
        validateField(name, value, index);

        const updatedHras = [...hras];
        updatedHras[index] = { ...updatedHras[index], [name]: value };
        setHras(updatedHras);

        let patchDocument = [{ op: 'replace', path: `/${name}`, value: value }];

        debouncedUpdate(patchDocument, updatedHras[index].planId);
    };

    const handleDateChange = (index, fieldName, date) => {
        // If the date is null or undefined (cleared), handle it accordingly
        if (!date) {
            // Clear the field in the state
            const updatedHras = [...hras];
            updatedHras[index] = { ...updatedHras[index], [fieldName]: '' }; // Clear the date field
            setHras(updatedHras);

            // Create the patch document for clearing the date
            let patchDocument = [{ op: 'replace', path: `/${fieldName}`, value: '' }];
            debouncedUpdate(patchDocument, updatedHras[index].planId);

            return;
        }

        // Normalize the date to prevent timezone issues
        const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        // Format the date as MM/dd/yyyy
        const formattedDate = localDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });

        // Perform field validation
        validateField(fieldName, formattedDate, index);

        // Update state with the selected date
        const updatedHras = [...hras];
        updatedHras[index] = { ...updatedHras[index], [fieldName]: formattedDate };
        setHras(updatedHras);

        // Create the patch document for the selected date
        let patchDocument = [{ op: 'replace', path: `/${fieldName}`, value: formattedDate }];

        // Debounced update to the backend
        debouncedUpdate(patchDocument, updatedHras[index].planId);
    };

    const handleToggleChange = (
        index,
        e,
        property
    ) => {
    
        const { checked } = e.target;
    
        const value = checked;
    
        const updatedHras = [...hras];
    
        const updatedHra = {
            ...updatedHras[index]
        };
    
        updatedHra[property] = value;
    
        let patchDocument = [
            {
                op: 'replace',
                path: `/${property}`,
                value: value
            }
        ];
    
        // Existing logic
        if (
            property === "isPlanChanging" &&
            !checked
        ) {
    
            updatedHra.isAutoRenew = false;
    
            patchDocument.push({
                op: 'replace',
                path: '/isAutoRenew',
                value: false
            });
        }
    
        // NEW LOGIC FOR TERMINATING
        if (property === "isTerminating") {
    
            // If terminating → auto set date
            if (checked && updatedHra.planStartDate) {
    
                const startDate = new Date(
                    updatedHra.planStartDate
                );
    
                const terminationDate =
                    new Date(startDate);
    
                terminationDate.setDate(
                    terminationDate.getDate() - 1
                );
    
                updatedHra.planTerminationDate =
                    terminationDate;
    
                patchDocument.push({
                    op: 'replace',
                    path: '/planTerminationDate',
                    value: terminationDate
                });
            }
    
            // If not terminating → clear date
            if (!checked) {
    
                updatedHra.planTerminationDate = '';
    
                patchDocument.push({
                    op: 'replace',
                    path: '/planTerminationDate',
                    value: ''
                });
            }
        }
    
        updatedHras[index] = updatedHra;
    
        setHras(updatedHras);
    
        debouncedUpdate(
            patchDocument,
            updatedHra.planId
        );
    };

    const handleAddPlan = async () => {
        try {

            const randomId = uuidv4();
            const firstPlanStartDate = hras.length > 0 ? hras[0].planStartDate : '';
            const firstPlanEndDate = hras.length > 0 ? hras[0].planEndDate : '';

            const newPlan = { ...initialFormValues, planId: caseId+"_"+randomId, planStartDate: firstPlanStartDate, planEndDate: firstPlanEndDate, caseId: caseId, isSameExpenses: false };

            const response = await axios.post(`${config.API_URL}/EBHRAPlans/`, newPlan);

            const addedPlan = {
                ...response.data,
                leftDivisions: expensesArray.filter(expense => !response.data.hraExpenses?.split(',').includes(expense)) || [],
                rightDivisions: response.data.hraExpenses ? response.data.hraExpenses.split(',') : [],
                selectedLeft: [],
                selectedRight: []
            };

            // Update the HRA state with the new plan
            setHras(prevHras => [...prevHras, addedPlan]);

        } catch (error) {
            console.error('Error adding new plan:', error);
            onError(error);
        }
    };

    const handleDeletePlan = async (index) => {
        alertconfirmation(
            `Do you want to delete this specific HRA Plan ?`,
            "Confirmation",
            async () => {
                try {
                    const planId = hras[index].planId;
                    await axios.delete(`${config.API_URL}/EBHRAPlans/${planId}`);
                    const updatedHras = hras.filter((_, i) => i !== index);
                    setHras(updatedHras);
                } catch (error) {
                    console.error('Error deleting plan:', error);
                    onError(error);
                }
            }
        );
    };



    const customStyles = (hasError) => ({
        control: (provided) => ({
            ...provided,
            borderColor: hasError ? '#dc3545' : provided.borderColor,
            '&:hover': {
                borderColor: hasError ? '#dc3545' : provided.borderColor,
            },
            boxShadow: hasError ? '0 0 10px rgba(220, 53, 69, 0.5)' : provided.boxShadow,
        }),
    });

    
    //const isDisabled = renewalCase?.ebSubmissionStatus === 'Submitted' || isDisableForUsers;
    const isDisabled =  isDisableForUsers;

    if (loading) return <Skeleton />;
    if (error) return <div>{error}</div>;

    return (
        <>
            {generalInfo?.isHRAClient && (<Col lg={12} className='mb-3'>
                <Card>
                    <Card.Body>
                        <h5 className="card-title">Health Reimbursement Arrangement (HRA)</h5>
                        {hras.map((hra, index) => (
                            <Card className='mb-3'>
                                <Card.Body>
                                    <div key={index}>
                                        {hra.isRenewal && (
                                            <>
                                                <Row className="row">
                                                    <span className="card-title">
                                                        <strong> {`${hra.planName}`}</strong>
                                                    </span>

                                                </Row>
                                                <Row className='mb-3'>
                                                    <Col lg={4}>
                                                        <div className="d-flex align-items-center">
                                                            <Form.Label>
                                                                Is this HRA Plan terminating?
                                                            </Form.Label>
                                                            <div className="form-check form-switch form-toggle">

                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input form-check-input-toggel toggle-input-switch"
                                                                    role="switch" value="true"
                                                                    checked={hra.isTerminating}
                                                                    onChange={(e) => handleToggleChange(index, e, 'isTerminating')}
                                                                    disabled={isDisabled}
                                                                />

                                                            </div>
                                                        </div>

                                                    </Col>


                                                    {hra.isTerminating && (
                                                        <Col lg={3}>
                                                            <div className="d-flex ">
                                                                <span className="input-group-text">
                                                                    <i className="  far fa-calendar-alt"></i>
                                                                </span>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={<Tooltip id="planTerminationDate-tooltip">Plan Termination Date</Tooltip>}
                                                                >
                                                                    <div>
                                                                        <DatePicker
                                                                            className={`form-control ${validationErrors[index] && validationErrors[index].planTerminationDate ? 'is-invalid' : ''}`}
                                                                            
                                                                            selected={
                                                                                hra?.planTerminationDate
                                                                                    ? new Date(
                                                                                        hra.planTerminationDate
                                                                                      )
                                                                                    : null
                                                                            }
                                                                            onChange={(date) => handleDateChange(index, 'planTerminationDate', date)}
                                                                            placeholderText="Plan Termination Date"
                                                                            disabled={isDisabled}
                                                                            minDate={
                                                                                hra.planStartDate
                                                                                    ? new Date(
                                                                                          new Date(hra.planStartDate).setDate(
                                                                                              new Date(
                                                                                                  hra.planStartDate
                                                                                              ).getDate() - 1
                                                                                          )
                                                                                      )
                                                                                    : null
                                                                            }
                                                                        />
                                                                    </div>
                                                                </OverlayTrigger>
                                                            </div>
                                                            {validationErrors[index] && validationErrors[index].planTerminationDate && (
                                                                <div className="invalid-feedback d-block">
                                                                    {validationErrors[index].planTerminationDate}
                                                                </div>
                                                            )}
                                                        </Col>
                                                    )}
                                                </Row>
                                            </>
                                        )}

                                        {!hra.isTerminating && (
                                            <>
                                                {hra.isRenewal && (
                                                    <>
                                                    <Row className='mb-3'>
                                                        <Col lg={12}>
                                                            <div className="d-flex align-items-center mb-2">
                                                                <Form.Label>
                                                                Is your HRA plan design staying the same for the new plan year?
                                                                </Form.Label>
                                                                {/* <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            Clarity can Auto-Renew the current HRA enrollment for existing participants in the HRA(s). To do so, log into the Administrator site and run the Enrolled Participants Report for the current plan year. Review and verify there are no enrollment/coverage tier changes for participants. if there are no changes, you may select "Yes" to Auto-Renew.
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger> */}
                                                                <div className="form-check form-switch form-toggle">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                                        role="switch"
                                                                        checked={hra?.isPlanChanging}
                                                                        onChange={(e) => handleToggleChange(index, e, 'isPlanChanging')}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>


                                                            </div>
                                                            
                                                        </Col>


                                                    </Row>
                                                    {hra?.isPlanChanging && (
                                                    <Row className='mb-3'>
                                                        <Col lg={12}>
                                                            <div className="d-flex align-items-center mb-2">
                                                                <Form.Label>
                                                                    HRA Auto-Renew
                                                                </Form.Label>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            Clarity can Auto-Renew the current HRA enrollment for existing participants in the HRA(s). To do so, log into the Administrator site and run the Enrolled Participants Report for the current plan year. Review and verify there are no enrollment/coverage tier changes for participants. if there are no changes, you may select "Yes" to Auto-Renew.
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger>
                                                                <div className="form-check form-switch form-toggle">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                                        role="switch"
                                                                        checked={hra.isAutoRenew}
                                                                        onChange={(e) => handleToggleChange(index, e, 'isAutoRenew')}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>


                                                            </div>
                                                            {hra.isAutoRenew && (

                                                                <div className="d-flex  mb-2 ">
                                                                    Yes, we have verified there are no changes to the existing HRA plan design, and the participants will remain the same at the same coverage tier. Please auto-renew participants "as-is" for the new plan year.
                                                                </div>

                                                            )}

                                                        </Col>


                                                    </Row>
                                                    )}
                                                    </>
                                                )}

                                                {!hra?.isPlanChanging && (
                                                    <>
                                                        {!hra.isRenewal && (
                                                            <>

                                                                <Row className='mb-3'>
                                                                    <Col lg={12}>
                                                                        HRA Type
                                                                    </Col>
                                                                </Row>
                                                                <Row className='mb-3'>
                                                                    <Col lg={12}>
                                                                        <div className="form-check-inline">
                                                                            <input
                                                                                type="radio"
                                                                                className="emp-form-check-input"
                                                                                value="Standard Integrated"
                                                                                name={`hraType_${index}`}
                                                                                id={`hraType_StandardIntegrated_${index}`}
                                                                                checked={hra?.hraType === "Standard Integrated"}
                                                                                onChange={(e) => handleRadioChange(index, 'hraType', e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                            <label htmlFor={`hraType_StandardIntegrated_${index}`}>Standard Integrated</label>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id={`tooltip-plan-rule-StandardIntegrated`}>
                                                                                        Most common HRA. Integrated with a traditional health insurance plan. Can be designed to cover deductibles, coinsurance, copays, uninsured medical expenses or specific expenses like pharmacy.
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span className="btn btn-sm btn-bg-soft-secondary ">
                                                                                    <i className="dropicons dripicons-information"></i>
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </div>

                                                                        <div className="form-check-inline">
                                                                            <input
                                                                                type="radio"
                                                                                className="emp-form-check-input"
                                                                                value="Limited Scope (Dental/Vision)"
                                                                                name={`hraType_${index}`}
                                                                                id={`hraType_LimitedScope_${index}`}
                                                                                checked={hra?.hraType === "Limited Scope (Dental/Vision)"}
                                                                                onChange={(e) => handleRadioChange(index, 'hraType', e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                            <label htmlFor={`hraType_LimitedScope_${index}`}>Limited Scope (Dental/Vision)</label>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id={`tooltip-plan-rule-LimitedScope`}>
                                                                                        Covers non-cosmetic dental and vision
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span className="btn btn-sm btn-bg-soft-secondary ">
                                                                                    <i className="dropicons dripicons-information"></i>
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </div>

                                                                        <div className="form-check-inline">
                                                                            <input
                                                                                type="radio"
                                                                                className="emp-form-check-input"
                                                                                value="Qualified Small Employer (QSEHRA)"
                                                                                name={`hraType_${index}`}
                                                                                id={`hraType_QSEHRA_${index}`}
                                                                                checked={hra?.hraType === "Qualified Small Employer (QSEHRA)"}
                                                                                onChange={(e) => handleRadioChange(index, 'hraType', e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                            <label htmlFor={`hraType_QSEHRA_${index}`}>Qualified Small Employer (QSEHRA)</label>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id={`tooltip-plan-rule-QSEHRA`}>
                                                                                        Employers (&lt;50 full-time employees) can set aside fixed tax-free amount monthly for employees to cover individual health insurance or medical expenses. Cannot offer employer-sponsored group health plan
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span className="btn btn-sm btn-bg-soft-secondary ">
                                                                                    <i className="dropicons dripicons-information"></i>
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </div>

                                                                        <div className="form-check-inline">
                                                                            <input
                                                                                type="radio"
                                                                                className="emp-form-check-input"
                                                                                value="Individual Coverage (ICHRA)"
                                                                                name={`hraType_${index}`}
                                                                                id={`hraType_ICHRA_${index}`}
                                                                                checked={hra?.hraType === "Individual Coverage (ICHRA)"}
                                                                                onChange={(e) => handleRadioChange(index, 'hraType', e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                            <label htmlFor={`hraType_ICHRA_${index}`}>Individual Coverage (ICHRA)</label>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id={`tooltip-plan-rule-ICHRA`}>
                                                                                        Employees must have individual health insurance coverage to qualify and cannot be eligible for employer-sponsored plan. No reimbursement limits. Groups &gt;50 employees must offer “affordable” reimbursement amounts
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span className="btn btn-sm btn-bg-soft-secondary ">
                                                                                    <i className="dropicons dripicons-information"></i>
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </div>

                                                                        <div className="form-check-inline">
                                                                            <input
                                                                                type="radio"
                                                                                className="emp-form-check-input"
                                                                                value="Excepted Benefits HRA (EBHRA)"
                                                                                name={`hraType_${index}`}
                                                                                id={`hraType_EBHRA_${index}`}
                                                                                checked={hra?.hraType === "Excepted Benefits HRA (EBHRA)"}
                                                                                onChange={(e) => handleRadioChange(index, 'hraType', e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                            <label htmlFor={`hraType_EBHRA_${index}`}>Excepted Benefits HRA (EBHRA)</label>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id={`tooltip-plan-rule-EBHRA`}>
                                                                                        Available to employees covered by the employer’s group health insurance. Employees can participate even if they decline group plan coverage. Benefits up to IRS limit
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span className="btn btn-sm btn-bg-soft-secondary ">
                                                                                    <i className="dropicons dripicons-information"></i>
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </div>
                                                                    </Col>
                                                                </Row>

                                                            </>
                                                        )}

                                                        <Row className='mb-3'>
                                                            <Col lg={3}>
                                                                <Form.Label>
                                                                    Plan Start Date
                                                                </Form.Label>
                                                            </Col>
                                                            <Col lg={3}>
                                                                <div className="d-flex ">
                                                                    <span className="input-group-text">
                                                                        <i className="  far fa-calendar-alt"></i>
                                                                    </span>
                                                                    <DatePicker
                                                                        className={`form-control ${validationErrors[index] && validationErrors[index].planStartDate ? 'is-invalid' : ''}`}
                                                                        selected={hra.planStartDate || ''}
                                                                        onChange={(date) => handleDateChange(index, 'planStartDate', date)}
                                                                        placeholderText="Plan Start Date"
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>
                                                                {validationErrors[index] && validationErrors[index].planStartDate && (
                                                                    <div className="invalid-feedback d-block">
                                                                        {validationErrors[index].planStartDate}
                                                                    </div>
                                                                )}
                                                            </Col>
                                                        </Row>
                                                        <Row className='mb-3'>
                                                            <Col lg={3}>
                                                                <Form.Label>
                                                                    Plan End Date
                                                                </Form.Label>
                                                            </Col>
                                                            <Col lg={3}>
                                                                <div className="d-flex ">
                                                                    <span className="input-group-text">
                                                                        <i className="  far fa-calendar-alt"></i>
                                                                    </span>

                                                                    <DatePicker
                                                                        className={`form-control ${validationErrors[index] && validationErrors[index].planEndDate ? 'is-invalid' : ''}`}
                                                                        selected={hra.planEndDate || ''}
                                                                        onChange={(date) => handleDateChange(index, 'planEndDate', date)}
                                                                        placeholderText="Plan End Date"
                                                                        minDate={hra.planStartDate ? new Date(hra.planStartDate) : null}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>
                                                                {validationErrors[index] && validationErrors[index].planEndDate && (
                                                                    <div className="invalid-feedback d-block">
                                                                        {validationErrors[index].planEndDate}
                                                                    </div>
                                                                )}
                                                            </Col>
                                                        </Row>
                                                        <Row className='mb-3'>
                                                            <Col lg={3}>
                                                                <Form.Label>
                                                                    Claims Runout Period for Active Employees
                                                                </Form.Label>
                                                            </Col>
                                                            <Col lg={3}>
                                                                <div className="d-flex ">
                                                                    <span className="input-group-text">
                                                                        <i className="  far fa-calendar-alt"></i>
                                                                    </span>

                                                                    <DatePicker
                                                                        className={`form-control ${validationErrors[index] && validationErrors[index].hraClaimsRunoutActiveEmp ? 'is-invalid' : ''}`}
                                                                        selected={hra.hraClaimsRunoutActiveEmp || ''}
                                                                        onChange={(date) => handleDateChange(index, 'hraClaimsRunoutActiveEmp', date)}
                                                                        placeholderText="Please select"
                                                                        minDate={new Date()}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>

                                                            </Col>
                                                        </Row>
                                                        <Row className='mb-5'>
                                                            <Col lg={3}>
                                                                <Form.Label>
                                                                    Claims Runout Period for Terminated Employees
                                                                </Form.Label>
                                                            </Col>
                                                            <Col lg={3}>

                                                                <Select
                                                                    className={`flex-grow-1 `}
                                                                    name="hraClaimsRunoutTerminatedEmp"
                                                                    options={options}
                                                                    value={options?.find(option => option.value === hra.hraClaimsRunoutTerminatedEmp)}
                                                                    placeholder="Please select"
                                                                    onChange={(selectedOption) => handleSelectChange(index, 'hraClaimsRunoutTerminatedEmp', selectedOption)}
                                                                    styles={customStyles(validationErrors[index] && validationErrors[index].hraClaimsRunoutTerminatedEmp)}
                                                                    isDisabled={isDisabled}
                                                                />
                                                            </Col>
                                                        </Row>

                                                        <Row className='mb-3'>
                                                        <Col lg={12}>
                            <div className="d-flex align-items-center">
                                <Form.Label>
                                Will the HRA reimburse same expenses as current plan year?
                                </Form.Label>
                                <div className="form-check form-switch form-toggle">
                                    <input
                                        type="checkbox"
                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                        role="switch"
                                        checked={hra.isSameExpenses}
                                        onChange={(e) => handleToggleChange(index, e, 'isSameExpenses')}
                                        disabled={isDisabled}
                                    />
                                </div>
                            </div>
                        </Col>
                                                        </Row>

                                                        {!hra.isSameExpenses && (
                                                            <>
 <Row>
                                                            <Col lg={12}>
                                                                <Form.Label>
                                                                    Please select all expenses reimbursable through this HRA
                                                                </Form.Label>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            In-network/out-of-network cannot be verified via Clarity Benefit Card and/or certain insurance provider&#x27;s EOBs.                                           </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </Col>
                                                        </Row>

                                                        <Row className="row mb-5 divisionsContainer">
                                                            <Col lg={12} className="expensesContainer container">
                                                                {/* Left Block: Available Expenses */}
                                                                <div className='block-container'>
                                                                    <label className='ms-2'><strong>Available</strong></label>
                                                                    <div className="block left-block">
                                                                        {hra.leftDivisions.map((division, idx) => (
                                                                            <p
                                                                                key={idx}
                                                                                data-index={division}
                                                                                onClick={() => toggleSelection(division, 'left', index)}  // Handle selection
                                                                                className={hra.selectedLeft.includes(division) ? 'selected' : ''}
                                                                            >
                                                                                {division}
                                                                            </p>
                                                                        ))}
                                                                    </div>
                                                                </div>


                                                                {/* Arrow Buttons to Move Items */}
                                                                <div className="arrow-container">
                                                                    <i
                                                                        className="arrow fas fa-arrow-right right-arrow"
                                                                        onClick={() => moveLeftToRight(index)} disabled={isDisabled}
                                                                    ></i>
                                                                    <i
                                                                        className="arrow fas fa-arrow-left left-arrow"
                                                                        onClick={() => moveRightToLeft(index)} disabled={isDisabled}
                                                                    ></i>
                                                                </div>

                                                                {/* Right Block: HRA Selected Expenses */}
                                                                <div className='block-container'>
                                                                    <label className='ms-3'><strong>Selected</strong></label>
                                                                    <div
                                                                        className="block right-block"
                                                                        style={
                                                                            validationErrors[index] && validationErrors[index].hraExpenses
                                                                                ? {
                                                                                    paddingRight: 'calc(1.5em + 0.75rem)',
                                                                                    boxShadow: 'rgba(220, 53, 69, 0.5) 0px 0px 10px',
                                                                                    borderColor: '#dc3545'
                                                                                }
                                                                                : {}
                                                                        }
                                                                    >
                                                                        {hra.rightDivisions.map((division, idx) => (
                                                                            <p
                                                                                key={idx}
                                                                                data-index={division}
                                                                                onClick={() => toggleSelection(division, 'right', index)} // Handle selection
                                                                                className={hra.selectedRight.includes(division) ? 'selected' : ''}
                                                                            >
                                                                                {division}
                                                                            </p>
                                                                        ))}
                                                                    </div>
                                                                    {validationErrors[index] && validationErrors[index].hraExpenses && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors[index].hraExpenses}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                            </Col>
                                                        </Row>
                                                            </>
                                                        )}


                                                       



                                                        <Row className='mb-3'>
                                                            <Col lg={3}>
                                                                <Form.Label>
                                                                    Carrier Name
                                                                </Form.Label>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            Pending with tooltip information
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </Col>
                                                            <Col lg={3}>

                                                                <Select
                                                                    className={`flex-grow-1 `}
                                                                    name="carrierName"
                                                                    options={carrierOptions}
                                                                    value={carrierOptions?.find(option => option.value === hra.carrierName)}
                                                                    placeholder="Please select"
                                                                    onChange={(selectedOption) => handleSelectChange(index, 'carrierName', selectedOption)}
                                                                    styles={customStyles(validationErrors[index] && validationErrors[index].carrierName)}
                                                                    isDisabled={isDisabled}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row className='mb-3'>
                                                            <Col lg={3}>
                                                                <Form.Label>
                                                                    Plan Name
                                                                </Form.Label>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            Pending with tooltip information
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </Col>
                                                            <Col lg={3}>
                                                                <Form.Control
                                                                    className={`form-control  ${validationErrors[index] && validationErrors[index].planName ? 'is-invalid' : ''} `}
                                                                    title="Plan name"
                                                                    type="text"
                                                                    name="planName"
                                                                    placeholder="Plan name"
                                                                    value={hra.planName || ''}
                                                                    onChange={(e) => handleInputChange(index, e)}
                                                                    disabled={isDisabled}
                                                                />
                                                                {validationErrors[index] && validationErrors[index].planName && (
                                                                    <div className="invalid-feedback d-block">
                                                                        {validationErrors[index].planName}
                                                                    </div>
                                                                )}
                                                            </Col>
                                                        </Row>
                                                    </>
                                                )}
                                                <Row className='mb-3'>
                                                    <Col lg={3}>
                                                        <Form.Label>
                                                            HRA Funding
                                                        </Form.Label>
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                                <Tooltip id={`tooltip-plan-rule-`}>
                                                                    Please enter the annual amount the company is funding for each tier.
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                <i className="dropicons dripicons-information"></i>
                                                            </span>
                                                        </OverlayTrigger>
                                                    </Col>
                                                </Row>
                                                <Row className='mb-3'>
                                                    <Col lg={3}>
                                                        <Form.Label>
                                                            Employee
                                                        </Form.Label>
                                                    </Col>
                                                    <Col lg={3}>
                                                        <Form.Control
                                                            className={`form-control  ${validationErrors[index] && validationErrors[index].employeeFunding ? 'is-invalid' : ''} `}
                                                            title="employeeFunding"
                                                            type="text"
                                                            name="employeeFunding"
                                                            placeholder="$ Employee"
                                                            value={hra.employeeFunding || ''}
                                                            onChange={(e) => handleInputChange(index, e)}
                                                            disabled={isDisabled}
                                                        />
                                                        {validationErrors[index] && validationErrors[index].employeeFunding && (
                                                            <div className="invalid-feedback d-block">
                                                                {validationErrors[index].employeeFunding}
                                                            </div>
                                                        )}
                                                    </Col>
                                                </Row>
                                                <Row className='mb-3'>
                                                    <Col lg={3}>
                                                        <Form.Label>
                                                            Employee/Child
                                                        </Form.Label>
                                                    </Col>
                                                    <Col lg={3}>
                                                        <Form.Control
                                                            className={`form-control  ${validationErrors[index] && validationErrors[index].employeeChildFunding ? 'is-invalid' : ''} `}
                                                            title="employeeChildFunding"
                                                            type="text"
                                                            name="employeeChildFunding"
                                                            placeholder="$ Employee/Child"
                                                            value={hra.employeeChildFunding || ''}
                                                            onChange={(e) => handleInputChange(index, e)}
                                                            disabled={isDisabled}
                                                        />
                                                        {validationErrors[index] && validationErrors[index].employeeChildFunding && (
                                                            <div className="invalid-feedback d-block">
                                                                {validationErrors[index].employeeChildFunding}
                                                            </div>
                                                        )}
                                                    </Col>
                                                </Row>
                                                <Row className='mb-3'>
                                                    <Col lg={3}>
                                                        <Form.Label>
                                                            Employee/Spouse
                                                        </Form.Label>
                                                    </Col>
                                                    <Col lg={3}>
                                                        <Form.Control
                                                            className={`form-control  ${validationErrors[index] && validationErrors[index].employeeSpouseFunding ? 'is-invalid' : ''} `}
                                                            title="employeeSpouseFunding"
                                                            type="text"
                                                            name="employeeSpouseFunding"
                                                            placeholder="$ Employee/Spouse"
                                                            value={hra.employeeSpouseFunding || ''}
                                                            onChange={(e) => handleInputChange(index, e)}
                                                            disabled={isDisabled}
                                                        />
                                                        {validationErrors[index] && validationErrors[index].employeeSpouseFunding && (
                                                            <div className="invalid-feedback d-block">
                                                                {validationErrors[index].employeeSpouseFunding}
                                                            </div>
                                                        )}
                                                    </Col>
                                                </Row>
                                                <Row className='mb-3'>
                                                    <Col lg={3}>
                                                        <Form.Label>
                                                            Family
                                                        </Form.Label>
                                                    </Col>
                                                    <Col lg={3}>
                                                        <Form.Control
                                                            className={`form-control  ${validationErrors[index] && validationErrors[index].employeeFamilyFunding ? 'is-invalid' : ''} `}
                                                            title="employeeFamilyFunding"
                                                            type="text"
                                                            name="employeeFamilyFunding"
                                                            placeholder="$ Family"
                                                            value={hra.employeeFamilyFunding || ''}
                                                            onChange={(e) => handleInputChange(index, e)}
                                                            disabled={isDisabled}
                                                        />
                                                        {validationErrors[index] && validationErrors[index].employeeFamilyFunding && (
                                                            <div className="invalid-feedback d-block">
                                                                {validationErrors[index].employeeFamilyFunding}
                                                            </div>
                                                        )}
                                                    </Col>
                                                </Row>

                                                {!hra?.isPlanChanging && (
                                                    <>
                                                        <Row className='mb-3'>
                                                            <Col lg={6}>
                                                                <Form.Label>
                                                                    Should the HRA contribution be pro-rated for mid-year enrollments?
                                                                </Form.Label>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            The system will not automatically calculate the pro-rated amount, if allowed. This will need to be provided upon enrollment.
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </Col>
                                                            <Col lg={6} className="d-flex align-items-start">
                                                                <div className="form-check form-switch form-toggle">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                                        role="switch"
                                                                        checked={hra.isProRatedMYE}
                                                                        onChange={(e) => handleToggleChange(index, e, 'isProRatedMYE')}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                        <Row className='mb-3'>
                                                            <Col lg={6}>
                                                                <Form.Label>
                                                                    Before the HRA pays, will the employee have any upfront responsibility?
                                                                </Form.Label>
                                                            </Col>
                                                            <Col lg={6} className="d-flex align-items-start">
                                                                <div className="form-check form-switch form-toggle">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                                        role="switch"
                                                                        checked={hra.isUpFrontRsp}
                                                                        onChange={(e) => handleToggleChange(index, e, 'isUpFrontRsp')}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                        {hra.isUpFrontRsp && (
                                                            <Row className="mb-3">
                                                                <Col lg={3}>
                                                                    <InputGroup>
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={<Tooltip id="tooltip-employeeUpFront">Employee Up Front</Tooltip>}
                                                                        >
                                                                            <Form.Control
                                                                                className={`form-control ${validationErrors[index] && validationErrors[index].employeeUpFront ? 'is-invalid' : ''}`}
                                                                                title="employeeUpFront"
                                                                                type="text"
                                                                                name="employeeUpFront"
                                                                                placeholder="$ Employee"
                                                                                value={hra.employeeUpFront || ''}
                                                                                onChange={(e) => handleInputChange(index, e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                        </OverlayTrigger>
                                                                    </InputGroup>
                                                                    {validationErrors[index] && validationErrors[index].employeeUpFront && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors[index].employeeUpFront}
                                                                        </div>
                                                                    )}
                                                                </Col>
                                                                <Col lg={3}>
                                                                    <InputGroup>
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={<Tooltip id="tooltip-employeeChildUpFront">Employee/Child Up Front</Tooltip>}
                                                                        >
                                                                            <Form.Control
                                                                                className={`form-control ${validationErrors[index] && validationErrors[index].employeeChildUpFront ? 'is-invalid' : ''}`}
                                                                                title="employeeChildUpFront"
                                                                                type="text"
                                                                                name="employeeChildUpFront"
                                                                                placeholder="$ Employee/Child"
                                                                                value={hra.employeeChildUpFront || ''}
                                                                                onChange={(e) => handleInputChange(index, e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                        </OverlayTrigger>
                                                                    </InputGroup>
                                                                    {validationErrors[index] && validationErrors[index].employeeChildUpFront && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors[index].employeeChildUpFront}
                                                                        </div>
                                                                    )}
                                                                </Col>
                                                                <Col lg={3}>
                                                                    <InputGroup>
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={<Tooltip id="tooltip-employeeSpouseUpFront">Employee/Spouse Up Front</Tooltip>}
                                                                        >
                                                                            <Form.Control
                                                                                className={`form-control ${validationErrors[index] && validationErrors[index].employeeSpouseUpFront ? 'is-invalid' : ''}`}
                                                                                title="employeeSpouseUpFront"
                                                                                type="text"
                                                                                name="employeeSpouseUpFront"
                                                                                placeholder="$ Employee/Spouse"
                                                                                value={hra.employeeSpouseUpFront || ''}
                                                                                onChange={(e) => handleInputChange(index, e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                        </OverlayTrigger>
                                                                    </InputGroup>
                                                                    {validationErrors[index] && validationErrors[index].employeeSpouseUpFront && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors[index].employeeSpouseUpFront}
                                                                        </div>
                                                                    )}
                                                                </Col>
                                                                <Col lg={3}>
                                                                    <InputGroup>
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={<Tooltip id="tooltip-employeeFamilyUpFront">Employee/Family Up Front</Tooltip>}
                                                                        >
                                                                            <Form.Control
                                                                                className={`form-control ${validationErrors[index] && validationErrors[index].employeeFamilyUpFront ? 'is-invalid' : ''}`}
                                                                                title="employeeFamilyUpFront"
                                                                                type="text"
                                                                                name="employeeFamilyUpFront"
                                                                                placeholder="$ Family"
                                                                                value={hra.employeeFamilyUpFront || ''}
                                                                                onChange={(e) => handleInputChange(index, e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                        </OverlayTrigger>
                                                                    </InputGroup>
                                                                    {validationErrors[index] && validationErrors[index].employeeFamilyUpFront && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors[index].employeeFamilyUpFront}
                                                                        </div>
                                                                    )}
                                                                </Col>
                                                            </Row>
                                                        )}

                                                        <Row className='mb-3'>
                                                            <Col lg={6}>
                                                                <Form.Label>
                                                                    Is any portion of HRA Contribution or employee responsibility embedded?
                                                                </Form.Label>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            Clarity Benefit Cards cannot be used with embedded deductibles. You will need to provide all dependent data on the enrollment file when embedded deductibles are used.
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </Col>
                                                            <Col lg={6} className="d-flex align-items-start">
                                                                <div className="form-check form-switch form-toggle">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                                        role="switch"
                                                                        checked={hra.isEmbedded}
                                                                        onChange={(e) => handleToggleChange(index, e, 'isEmbedded')}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                        {hra.isEmbedded && (
                                                            <Row className="mb-3">
                                                                <Col lg={2}>
                                                                    <InputGroup>
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={<Tooltip id="tooltip-employeeEmbdedded">Employee Embedded</Tooltip>}
                                                                        >
                                                                            <Form.Control
                                                                                className={`form-control ${validationErrors[index] && validationErrors[index].employeeEmbdedded ? 'is-invalid' : ''}`}
                                                                                title="employeeEmbdedded"
                                                                                type="text"
                                                                                name="employeeEmbdedded"
                                                                                placeholder="$ Employee"
                                                                                value={hra.employeeEmbdedded || ''}
                                                                                onChange={(e) => handleInputChange(index, e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                        </OverlayTrigger>
                                                                    </InputGroup>
                                                                    {validationErrors[index] && validationErrors[index].employeeEmbdedded && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors[index].employeeEmbdedded}
                                                                        </div>
                                                                    )}
                                                                </Col>
                                                                <Col lg={2}>
                                                                    <InputGroup>
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={<Tooltip id="tooltip-employeeChildEmbdedded">Employee/Child Embedded</Tooltip>}
                                                                        >
                                                                            <Form.Control
                                                                                className={`form-control ${validationErrors[index] && validationErrors[index].employeeChildEmbdedded ? 'is-invalid' : ''}`}
                                                                                title="employeeChildEmbdedded"
                                                                                type="text"
                                                                                name="employeeChildEmbdedded"
                                                                                placeholder="$ Employee/Child"
                                                                                value={hra.employeeChildEmbdedded || ''}
                                                                                onChange={(e) => handleInputChange(index, e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                        </OverlayTrigger>
                                                                    </InputGroup>
                                                                    {validationErrors[index] && validationErrors[index].employeeChildEmbdedded && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors[index].employeeChildEmbdedded}
                                                                        </div>
                                                                    )}
                                                                </Col>
                                                                <Col lg={2}>
                                                                    <InputGroup>
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={<Tooltip id="tooltip-employeeSpouseEmbdedded">Employee/Spouse Embedded</Tooltip>}
                                                                        >
                                                                            <Form.Control
                                                                                className={`form-control ${validationErrors[index] && validationErrors[index].employeeSpouseEmbdedded ? 'is-invalid' : ''}`}
                                                                                title="employeeSpouseEmbdedded"
                                                                                type="text"
                                                                                name="employeeSpouseEmbdedded"
                                                                                placeholder="$ Employee/Spouse"
                                                                                value={hra.employeeSpouseEmbdedded || ''}
                                                                                onChange={(e) => handleInputChange(index, e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                        </OverlayTrigger>
                                                                    </InputGroup>
                                                                    {validationErrors[index] && validationErrors[index].employeeSpouseEmbdedded && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors[index].employeeSpouseEmbdedded}
                                                                        </div>
                                                                    )}
                                                                </Col>
                                                                <Col lg={2}>
                                                                    <InputGroup>
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={<Tooltip id="tooltip-employeeFamilyEmbdedded">Employee/Family Embedded</Tooltip>}
                                                                        >
                                                                            <Form.Control
                                                                                className={`form-control ${validationErrors[index] && validationErrors[index].employeeFamilyEmbdedded ? 'is-invalid' : ''}`}
                                                                                title="employeeFamilyEmbdedded"
                                                                                type="text"
                                                                                name="employeeFamilyEmbdedded"
                                                                                placeholder="$ Family"
                                                                                value={hra.employeeFamilyEmbdedded || ''}
                                                                                onChange={(e) => handleInputChange(index, e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                        </OverlayTrigger>
                                                                    </InputGroup>
                                                                    {validationErrors[index] && validationErrors[index].employeeFamilyEmbdedded && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors[index].employeeFamilyEmbdedded}
                                                                        </div>
                                                                    )}
                                                                </Col>
                                                                <Col lg={3}>
                                                                    <InputGroup>
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={<Tooltip id="tooltip-maxReimbursementEmbedded">What is the maximum reimbursement for any one covered member in the plan?</Tooltip>}
                                                                        >
                                                                            <Form.Control
                                                                                className={`form-control ${validationErrors[index] && validationErrors[index].maxReimbursementEmbedded ? 'is-invalid' : ''}`}
                                                                                title="maxReimbursementEmbedded"
                                                                                type="text"
                                                                                name="maxReimbursementEmbedded"
                                                                                placeholder="What is the maximum reimbursement for any one covered member in the plan?"
                                                                                value={hra.maxReimbursementEmbedded || ''}
                                                                                onChange={(e) => handleInputChange(index, e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                        </OverlayTrigger>
                                                                    </InputGroup>
                                                                    {validationErrors[index] && validationErrors[index].maxReimbursementEmbedded && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors[index].maxReimbursementEmbedded}
                                                                        </div>
                                                                    )}
                                                                </Col>
                                                            </Row>
                                                        )}

                                                        <Row className='mb-3'>
                                                            <Col lg={6}>
                                                                <Form.Label>
                                                                    Is the employee responsible to pay a portion of eligible expenses under the HRA (cost sharing of claims)?
                                                                </Form.Label>
                                                            </Col>
                                                            <Col lg={6} className="d-flex align-items-start">
                                                                <div className="form-check form-switch form-toggle">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                                        role="switch"
                                                                        checked={hra.isEECostSharing}
                                                                        onChange={(e) => handleToggleChange(index, e, 'isEECostSharing')}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                        {hra.isEECostSharing && (
                                                            <Row className='mb-3'>
                                                                <Col lg={6}>
                                                                    <Form.Label>
                                                                        What percentage of each eligible expense is the employee responsible for?
                                                                    </Form.Label>
                                                                </Col>
                                                                <Col lg={2} className="d-flex align-items-start">
                                                                    <InputGroup>
                                                                        <Form.Control
                                                                            className={`form-control ${validationErrors[index] && validationErrors[index].employeeCostShare ? 'is-invalid' : ''}`}
                                                                            title="employeeCostShare"
                                                                            type="text"
                                                                            name="employeeCostShare"
                                                                            placeholder="%"
                                                                            value={hra.employeeCostShare || ''}
                                                                            onChange={(e) => handleInputChange(index, e)}
                                                                            disabled={isDisabled}
                                                                        />
                                                                        {validationErrors[index] && validationErrors[index].employeeCostShare && (
                                                                            <div className="invalid-feedback d-block">
                                                                                {validationErrors[index].employeeCostShare}
                                                                            </div>
                                                                        )}
                                                                    </InputGroup>
                                                                </Col>
                                                            </Row>
                                                        )}
                                                        <Row className='mb-3'>
                                                            <Col lg={5}>
                                                                <Form.Label>
                                                                    How will unused funds remaining in the HRA be handled?
                                                                </Form.Label>
                                                            </Col>
                                                            <Col lg={3} className="d-flex flex-column">
                                                                <fieldset>
                                                                    <div className="form-check-inline mb-2">
                                                                        <input
                                                                            type="radio"
                                                                            className="emp-form-check-input"
                                                                            value="Forfeited"
                                                                            name={`unusedFundHandling_${index}`}
                                                                            id={`unusedFundHandling_Forfeited_${index}`}
                                                                            checked={hra?.unusedFundHandling === "Forfeited"}
                                                                            onChange={(e) => handleRadioChange(index, 'unusedFundHandling', e)}
                                                                            disabled={isDisabled}
                                                                        />
                                                                        <label htmlFor={`unusedFundHandling_Forfeited_${index}`} className='ms-2'>Forfeited</label>
                                                                    </div>
                                                                    <div className="form-check-inline">
                                                                        <input
                                                                            type="radio"
                                                                            className="emp-form-check-input"
                                                                            value="RolledOver"
                                                                            name={`unusedFundHandling_${index}`}
                                                                            id={`unusedFundHandling_RolledOver_${index}`}
                                                                            checked={hra?.unusedFundHandling === "RolledOver"}
                                                                            onChange={(e) => handleRadioChange(index, 'unusedFundHandling', e)}
                                                                            disabled={isDisabled}
                                                                        />
                                                                        <label htmlFor={`unusedFundHandling_RolledOver_${index}`} className='ms-2'>Rolled over the next year plan</label>
                                                                    </div>
                                                                </fieldset>
                                                            </Col>
                                                            {hra?.unusedFundHandling === "RolledOver" && (
                                                                <>
                                                                    <Col lg={2}>
                                                                        <InputGroup>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip id="tooltip-unusedMaxRollAmt">Maximum Rollover Amount($)</Tooltip>}
                                                                            >
                                                                                <Form.Control
                                                                                    className={`form-control ${validationErrors[index] && validationErrors[index].unusedMaxRollAmt ? 'is-invalid' : ''}`}
                                                                                    title="unusedMaxRollAmt"
                                                                                    type="text"
                                                                                    name={`unusedMaxRollAmt_${index}`}
                                                                                    placeholder="Maximum Rollover Amount($)"
                                                                                    value={hra.unusedMaxRollAmt || ''}
                                                                                    onChange={(e) => handleRadioChange(index, 'unusedMaxRollAmt', e)}
                                                                                    disabled={isDisabled}
                                                                                />
                                                                            </OverlayTrigger>
                                                                            {validationErrors[index] && validationErrors[index].unusedMaxRollAmt && (
                                                                                <div className="invalid-feedback d-block">
                                                                                    {validationErrors[index].unusedMaxRollAmt}
                                                                                </div>
                                                                            )}
                                                                        </InputGroup>
                                                                    </Col>
                                                                    <Col lg={2}>
                                                                        <InputGroup>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip id="tooltip-unusedFundPCT">Percentage of Unused Funds(%)</Tooltip>}
                                                                            >
                                                                                <Form.Control
                                                                                    className={`form-control ${validationErrors[index] && validationErrors[index].unusedFundPCT ? 'is-invalid' : ''}`}
                                                                                    title="unusedFundPCT"
                                                                                    type="text"
                                                                                    name={`unusedFundPCT_${index}`}
                                                                                    placeholder="Percentage of Unused Funds(%)"
                                                                                    value={hra.unusedFundPCT || ''}
                                                                                    onChange={(e) => handleRadioChange(index, 'unusedFundPCT', e)}
                                                                                    disabled={isDisabled}
                                                                                />
                                                                            </OverlayTrigger>
                                                                            {validationErrors[index] && validationErrors[index].unusedFundPCT && (
                                                                                <div className="invalid-feedback d-block">
                                                                                    {validationErrors[index].unusedFundPCT}
                                                                                </div>
                                                                            )}
                                                                        </InputGroup>
                                                                    </Col>
                                                                </>
                                                            )}
                                                        </Row>

                                                        <Row className='mb-3'>
                                                            <Col lg={5}>
                                                                <Form.Label>
                                                                    If both HRA and FSA/LPFSA plans are offered, which plan will pay first?
                                                                </Form.Label>
                                                            </Col>
                                                            <Col lg={6} className='d-flex flex-column'>
                                                                <fieldset>
                                                                    <div className="form-check-inline mb-2">
                                                                        <input
                                                                            type="radio"
                                                                            className="emp-form-check-input"
                                                                            value="HRA"
                                                                            name={`hrafsaPriority_${index}`}
                                                                            id={`hrafsaPriority_HRA_${index}`}
                                                                            checked={hra?.hrafsaPriority === "HRA"}
                                                                            onChange={(e) => handleRadioChange(index, 'hrafsaPriority', e)}
                                                                            disabled={isDisabled}
                                                                        />
                                                                        <label htmlFor={`hrafsaPriority_HRA_${index}`} className='ms-2'>HRA</label>
                                                                    </div>
                                                                    <div className="form-check-inline">
                                                                        <input
                                                                            type="radio"
                                                                            className="emp-form-check-input"
                                                                            value="FSA"
                                                                            name={`hrafsaPriority_${index}`}
                                                                            id={`hrafsaPriority_FSA_${index}`}
                                                                            checked={hra?.hrafsaPriority === "FSA"}
                                                                            onChange={(e) => handleRadioChange(index, 'hrafsaPriority', e)}
                                                                            disabled={isDisabled}
                                                                        />
                                                                        <label htmlFor={`hrafsaPriority_FSA_${index}`} className='ms-2'>FSA</label>
                                                                    </div>
                                                                </fieldset>
                                                            </Col>
                                                        </Row>

                                                        <Row className='mb-3'>
                                                            <Col lg={5}>
                                                                <div className="d-flex align-items-center">
                                                                    <Form.Label className="mb-0">
                                                                        Add HRA plan to Clarity Benefit Card
                                                                    </Form.Label>
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={<Tooltip id={`tooltip-plan-rule_${index}`}>Additional fees may apply</Tooltip>}
                                                                    >
                                                                        <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                            <i className="dropicons dripicons-information"></i>
                                                                        </span>
                                                                    </OverlayTrigger>
                                                                    <div className="form-check form-switch form-toggle">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input form-check-input-toggel toggle-input-switch"
                                                                            role="switch"
                                                                            checked={hra.isHRACarded}
                                                                            onChange={(e) => handleToggleChange(index, e, 'isHRACarded')}
                                                                            disabled={isDisabled}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                            {hra.isHRACarded && (
                                                                <Col lg={6} className='d-flex flex-column'>
                                                                    <fieldset>
                                                                        <div className="form-check-inline mb-2">
                                                                            <input
                                                                                type="radio"
                                                                                className="emp-form-check-input"
                                                                                value="CardForAll"
                                                                                name={`hraCardOption_${index}`}
                                                                                id={`hraCardOption_CardForAll_${index}`}
                                                                                checked={hra?.hraCardOption === "CardForAll"}
                                                                                onChange={(e) => handleRadioChange(index, 'hraCardOption', e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                            <label htmlFor={`hraCardOption_CardForAll_${index}`} className='ms-2'>Card For All Eligible Expenses</label>
                                                                        </div>
                                                                        <div className="form-check-inline">
                                                                            <input
                                                                                type="radio"
                                                                                className="emp-form-check-input"
                                                                                value="CardForRx"
                                                                                name={`hraCardOption_${index}`}
                                                                                id={`hraCardOption_CardForRx_${index}`}
                                                                                checked={hra?.hraCardOption === "CardForRx"}
                                                                                onChange={(e) => handleRadioChange(index, 'hraCardOption', e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                            <label htmlFor={`hraCardOption_CardForRx_${index}`} className='ms-2'>Card For RX Only</label>
                                                                        </div>
                                                                    </fieldset>
                                                                </Col>
                                                            )}
                                                        </Row>

                                                        <Row className='mb-3'>
                                                            <Col lg={5}>
                                                                <div className="d-flex align-items-center">
                                                                    <Form.Label>
                                                                        Do you want cards to be issued to dependents?
                                                                    </Form.Label>
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={
                                                                            <Tooltip id={`tooltip-plan-rule_${index}`}>
                                                                                If you select "All Dependents" every dependent in the system regardless of age will be issued a separate Benefit Card. "Additional card fees may apply. In order for a Clarity Benefit Card to be issued to a dependent, they must be in our system.
                                                                            </Tooltip>
                                                                        }
                                                                    >
                                                                        <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                            <i className="dropicons dripicons-information"></i>
                                                                        </span>
                                                                    </OverlayTrigger>
                                                                    <div className="form-check form-switch form-toggle">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input form-check-input-toggel toggle-input-switch"
                                                                            role="switch"
                                                                            checked={hra.isHRADependentCarded}
                                                                            onChange={(e) => handleToggleChange(index, e, 'isHRADependentCarded')}
                                                                            disabled={isDisabled}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                            {hra.isHRADependentCarded && (
                                                                <Col lg={6} className='d-flex flex-column'>
                                                                    <fieldset>
                                                                        <div className="form-check-inline mb-2">
                                                                            <input
                                                                                type="radio"
                                                                                className="emp-form-check-input"
                                                                                value="SpouseOnly"
                                                                                name={`hraDependentCardOption_${index}`}
                                                                                id={`hraDependentCardOption_SpouseOnly_${index}`}
                                                                                checked={hra?.hraDependentCardOption === "SpouseOnly"}
                                                                                onChange={(e) => handleRadioChange(index, 'hraDependentCardOption', e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                            <label htmlFor={`hraDependentCardOption_SpouseOnly_${index}`} className='ms-2'>Spouse Only</label>
                                                                        </div>
                                                                        <div className="form-check-inline">
                                                                            <input
                                                                                type="radio"
                                                                                className="emp-form-check-input"
                                                                                value="AllDependents"
                                                                                name={`hraDependentCardOption_${index}`}
                                                                                id={`hraDependentCardOption_AllDependents_${index}`}
                                                                                checked={hra?.hraDependentCardOption === "AllDependents"}
                                                                                onChange={(e) => handleRadioChange(index, 'hraDependentCardOption', e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                            <label htmlFor={`hraDependentCardOption_AllDependents_${index}`} className='ms-2'>All Dependents</label>
                                                                        </div>
                                                                        {validationErrors[index] && validationErrors[index].hraDependentCardOption && (
                                                                            <div className="invalid-feedback d-block">
                                                                                {validationErrors[index].hraDependentCardOption}
                                                                            </div>
                                                                        )}
                                                                    </fieldset>
                                                                </Col>
                                                            )}
                                                        </Row>


                                                        <Row className='mb-3'>
                                                            <Col lg={4}>
                                                                <Form.Label>
                                                                    Additional Plan Details
                                                                </Form.Label>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            List any changes to the plan that have not been idenfied (i.e., HRA pays first, then employee pays, then HRA pays again).
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </Col>
                                                            <Col lg={6}>
                                                                <Form.Control

                                                                    className={`form-control  `}
                                                                    title="planAdditionanDetails"
                                                                    as="textarea"
                                                                    rows={2}
                                                                    name="planAdditionanDetails"
                                                                    placeholder=""
                                                                    value={hra.planAdditionanDetails || ''}
                                                                    onChange={(e) => handleInputChange(index, e)}
                                                                    disabled={isDisabled}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </>
                                                )}

                                            </>
                                        )}
                                    </div>
                                </Card.Body>
                                <Card.Footer >
                                    <Row className='mb-3'>
                                        <Col lg={8} className='d-flex align-items-center'>

                                        </Col>
                                        {!hra.isRenewal && (
                                            <Col lg={4}>

                                                <button className="delete-plan-info" onClick={() => handleDeletePlan(index)} disabled={isDisabled}>
                                                    <span className="fa fa-trash-alt me-1" id="delete"></span>
                                                </button>
                                            </Col>
                                        )}

                                    </Row>
                                </Card.Footer>
                            </Card>
                        ))}



                    </Card.Body>
                    <Card.Footer >
                        <Row className='mb-3'>
                            <Col lg={8} className='d-flex align-items-center'>
                                <Form.Label className='mb-0 me-3'>
                                    Do you have another medical plan that has a separate HRA?
                                </Form.Label>
                                <Button
                                    className="btn btn-success btn-sm"
                                    onClick={handleAddPlan} disabled={isDisabled}
                                >
                                    ADD HRA
                                </Button>
                            </Col>
                            <Col lg={4}>

                                {/* <Button className="delete-plan-info" onClick={() => handleDeletePlan(index)} disabled={isDisabled} >
                                                                    <span className="fa fa-trash-alt me-1" id="delete"></span>
                                                                </Button> */}
                            </Col>
                        </Row>
                    </Card.Footer>
                </Card>
            </Col>)}
        </>


    );
});

export default HRA;